import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Menu, Space, Typography, Breadcrumb, Drawer, Grid, Button } from 'antd';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { KeyboardShortcutsHelp } from '@/components/KeyboardShortcutsHelp';
import { translateMenuItems } from '@/i18n/translateMenu';
import { PushNotificationListener } from '@/components/notifications/PushNotificationListener';
import { useNotificationRealtime } from '@/hooks/useNotificationRealtime';
import {
  QuestionCircleOutlined,
  DashboardOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  InboxOutlined,
  ToolOutlined,
  ProjectOutlined,
  TeamOutlined,
  FileTextOutlined,
  BarChartOutlined,
  SettingOutlined,
  BellOutlined,
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CloseOutlined,
  SearchOutlined,
  ClockCircleOutlined,
  BankOutlined,
  UserAddOutlined,
  RocketOutlined,
  AppstoreOutlined,
  ApartmentOutlined,
  HomeOutlined,
  EditOutlined,
  FormOutlined,
  SwapOutlined,
  LinkOutlined,
  UploadOutlined,
  ShopOutlined,
  DesktopOutlined,
  CalendarOutlined,
  AccountBookOutlined,
  FileProtectOutlined,
  SolutionOutlined,
  IdcardOutlined,
  AuditOutlined,
  CameraOutlined,
  WarningOutlined,
  SafetyCertificateOutlined,
  BranchesOutlined,
  NumberOutlined,
  KeyOutlined,
  MailOutlined,
  HeartOutlined,
  TableOutlined,
  CalculatorOutlined,
  LineChartOutlined,
  TrophyOutlined,
  StarFilled,
  RightOutlined,
  CheckCircleOutlined,
  CarOutlined,
  ReadOutlined,
  CloseCircleOutlined,
  ReconciliationOutlined,
  PartitionOutlined,
  PlayCircleOutlined,
  TagsOutlined,
  RobotOutlined,
  GoldOutlined,
  FolderOutlined,
  QrcodeOutlined,
  MinusCircleOutlined,
  FileSearchOutlined,
  ApiOutlined,
  ExportOutlined,
  FlagOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { KeepAliveOutlet } from '@/layouts/KeepAliveOutlet';
import { TopNavigationBar } from '@/components/TopNavigationBar';
import { CommandPalette } from '@/components/CommandPalette';
import { useNavigation } from '@/contexts/NavigationContext';
import { PageBreadcrumbProvider, usePageBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import { useFavorites } from '@/hooks/useFavorites';
import { useNavigationShortcuts } from '@/hooks/useNavigationShortcuts';
import { EnterpriseTokens } from '@/theme/enterprise-tokens';
import { useAuthStore } from '@/store/authStore';
import { CompanySwitcher } from '@/components/auth/CompanySwitcher';
import { canAccessRouteByPermissions } from '@/config/routePermissions';
import { isPlatformSuperAdmin } from '@/utils/authUser';
import { useInstalledSolutionPacks } from '@/hooks/useInstalledSolutionPacks';
import { useProductBrand, useProductDocumentTitle } from '@/hooks/useProductBrand';
import { isNavPathAllowedByPacks } from '@/config/solutionPackNavGates';
import { AiAgentProvider } from '@/contexts/AiAgentContext';
import { IntelligenceSidebar } from '@/components/ai-agent/IntelligenceSidebar';
import { IntelligenceToolbarButton } from '@/components/ai-agent/IntelligenceToolbarButton';
import { AgentDrawer } from '@/components/ai-agent/AgentDrawer';

const { Header, Sider, Content } = Layout;

function filterMenuItemsByRouteAccess(
  items: NonNullable<MenuProps['items']>,
  canAccess: (path: string) => boolean
): MenuProps['items'] {
  const out: any[] = [];
  const list = items || [];
  let i = 0;
  while (i < list.length) {
    const it = list[i] as { type?: string; label?: string; key?: string };
    if (it?.type === 'group') {
      const label = it.label;
      i += 1;
      const block: any[] = [];
      while (i < list.length) {
        const next = list[i] as { type?: string; key?: string };
        if (next?.type === 'group') break;
        const sub = list[i] as { key?: string };
        if (sub?.key && typeof sub.key === 'string' && canAccess(sub.key)) {
          block.push(list[i]!);
        }
        i += 1;
      }
      if (block.length) {
        out.push({ type: 'group', label }, ...block);
      }
    } else {
      if (it?.key && typeof it.key === 'string' && canAccess(it.key)) {
        out.push(list[i]!);
      }
      i += 1;
    }
  }
  return out as MenuProps['items'];
}

const { Text } = Typography;

const DashboardLayoutInner: React.FC = () => {
  useNotificationRealtime();
  const { t, i18n } = useTranslation(['sidebar', 'nav', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const brand = useProductBrand();
  useProductDocumentTitle(brand);
  const { segments: pageBreadcrumbSegments } = usePageBreadcrumb();
  const { activeModule } = useNavigation();
  const { favorites } = useFavorites();
  const user = useAuthStore((s) => s.user);
  const superAdmin = isPlatformSuperAdmin(user);
  const { installedPackKeys } = useInstalledSolutionPacks();
  const canAccessPath = (path: string) => {
    const bare = String(path || '').split('?')[0] || path;
    return (
      canAccessRouteByPermissions(bare, user?.permissions ?? [], superAdmin) &&
      isNavPathAllowedByPacks(bare, installedPackKeys)
    );
  };
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  /* MODERN UPGRADE: Align breakpoints with Ant Design (md=768, lg=992) for tablet spacing */
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const isTablet = Boolean(screens.md && !screens.lg);

  // Setup keyboard shortcuts
  useNavigationShortcuts({
    onCommandK: () => setCommandPaletteOpen(true),
    onEscape: () => setCommandPaletteOpen(false),
  });

  // Contextual menu configuration - Oracle-style 10 domains
  const contextualMenus: Record<string, { label: string; items: MenuProps['items'] }> = {
    home: {
      label: 'Home',
      items: [
        { type: 'group', label: 'My Work' },
        { key: '/dashboard', label: 'My Workboard', icon: <DashboardOutlined /> },
        { key: '/home/tasks', label: 'My Tasks', icon: <CheckCircleOutlined /> },
        { key: '/home/approvals', label: 'Approval inbox', icon: <InboxOutlined /> },
        { key: '/home/team-tasks', label: 'Team workboard', icon: <TeamOutlined /> },
        { key: '/home/drafts', label: 'My Drafts', icon: <FileTextOutlined /> },
        { key: '/home/alerts', label: 'My Alerts', icon: <BellOutlined /> },
        { type: 'group', label: 'Operational Overview' },
        { key: '/home/company-overview', label: 'Company Overview', icon: <DashboardOutlined /> },
        { key: '/unified', label: 'Unified Execution Dashboard', icon: <DashboardOutlined /> },
        { key: '/home/exceptions', label: 'Management Exceptions', icon: <WarningOutlined /> },
        { type: 'group', label: 'Reports' },
        { key: '/reports', label: 'All reports', icon: <BarChartOutlined /> },
        { key: '/reports/audit', label: 'Report audit', icon: <FileTextOutlined /> },
      ],
    },

    sales: {
      label: 'Growth & Revenue',
      items: [
        { type: 'group', label: 'Workboard' },
        { key: '/growth/dashboard', label: 'Growth & Revenue Dashboard', icon: <DashboardOutlined /> },
        { type: 'group', label: 'Lead to quote' },
        { key: '/growth/leads', label: 'Leads', icon: <UserAddOutlined /> },
        { key: '/growth/opportunities', label: 'Opportunities', icon: <RocketOutlined /> },
        { key: '/growth/calendar', label: 'Sales calendar', icon: <CalendarOutlined /> },
        { key: '/growth/activities', label: 'Daily Sales Activity Log', icon: <CalendarOutlined /> },
        { key: '/growth/follow-ups', label: 'Follow-ups & Commitments', icon: <CheckCircleOutlined /> },
        { key: '/growth/standup', label: 'Daily Stand-Up', icon: <TeamOutlined /> },
        { key: '/growth/demos', label: 'Demos & Requirements', icon: <AuditOutlined /> },
        { key: '/growth/order-review-checklists', label: 'Enquiry Checklist Register', icon: <AuditOutlined /> },
        { key: '/growth/quotations', label: 'Quotations', icon: <FileTextOutlined /> },
        { key: '/growth/contract-reviews', label: 'Contract Review', icon: <FileTextOutlined /> },
        { type: 'group', label: 'Estimation' },
        { key: '/growth/estimations/boq', label: 'BOQ Estimation', icon: <CalculatorOutlined /> },
        { key: '/growth/estimations/assembly', label: 'Assembly-Based Estimation', icon: <CalculatorOutlined /> },
        { key: '/growth/estimations/trading-rental', label: 'Trading & Rental Estimation', icon: <CalculatorOutlined /> },
        { type: 'group', label: 'Transactions' },
        { key: '/growth/quotation-consolidation', label: 'CO from Quotations', icon: <FileTextOutlined /> },
        { key: '/growth/orders', label: 'Customer Orders', icon: <ShoppingOutlined /> },
        { key: '/growth/order-health', label: 'Order health hub', icon: <LineChartOutlined /> },
        {
          key: '/growth/inland-process-deviations',
          label: 'Inland process deviations',
          icon: <BranchesOutlined />,
        },
        { key: '/growth/invoices', label: 'Tax Invoices', icon: <FileTextOutlined /> },
        { key: '/growth/order-timesheets', label: 'Timesheet for Rental', icon: <CalendarOutlined /> },
        { key: '/growth/delivery-notes', label: 'Delivery notes', icon: <InboxOutlined /> },
        { key: '/growth/document-collections', label: 'Document collection', icon: <FileTextOutlined /> },
        { key: '/growth/delivery-readiness', label: 'Delivery readiness', icon: <InboxOutlined /> },
        { key: '/growth/my-deliveries', label: 'My deliveries', icon: <CheckCircleOutlined /> },
        { key: '/growth/packing-list', label: 'Packing list', icon: <TableOutlined /> },
        { type: 'group', label: 'Masters' },
        { key: '/growth/customers', label: 'Customers', icon: <TeamOutlined /> },
        {
          key: '/growth/customer-satisfaction',
          label: 'Customer Satisfaction Register',
          icon: <CheckCircleOutlined />,
        },
        { key: '/growth/customer-success', label: 'Customer Success', icon: <CheckCircleOutlined /> },
        { key: '/growth/setup/quotation-defaults', label: 'Quotation defaults', icon: <SettingOutlined /> },
        { key: '/growth/setup/activity-types', label: 'CRM activity types', icon: <SettingOutlined /> },
        { key: '/growth/sales-targets', label: 'Sales targets', icon: <LineChartOutlined /> },
        { key: '/cspm', label: 'Externally Owned Property', icon: <FileProtectOutlined /> },
        { key: '/platform/foundation/revenue-service-types', label: 'Revenue service types', icon: <GoldOutlined /> },
        {
          key: '/mfg/operations',
          label: 'Operations Master',
          icon: <PartitionOutlined />,
        },
        { type: 'group', label: 'Reports' },
        { key: '/growth/reports', label: 'Sales reports hub', icon: <BarChartOutlined /> },
        { key: '/growth/dashboards/team-command-center', label: 'Team command center', icon: <DashboardOutlined /> },
        { key: '/growth/reports/weekly-sales-performance', label: 'Weekly sales performance', icon: <BarChartOutlined /> },
        { key: '/growth/reports/conversion-funnel', label: 'Conversion funnel', icon: <BarChartOutlined /> },
        { key: '/growth/reports/win-loss', label: 'Win / loss', icon: <BarChartOutlined /> },
        { key: '/growth/reports/opportunity-conversion', label: 'Opportunity conversion', icon: <BarChartOutlined /> },
        { key: '/growth/reports/salesperson-performance', label: 'Salesperson performance', icon: <BarChartOutlined /> },
        { key: '/growth/reports/product-rst-mix', label: 'Product / RST mix', icon: <BarChartOutlined /> },
        { key: '/growth/reports/booking-billing-collections', label: 'Booking / billing / collections', icon: <BarChartOutlined /> },
        { key: '/growth/reports/invoice-register', label: 'Invoice register', icon: <FileTextOutlined /> },
        { key: '/growth/reports/order-register', label: 'Order register', icon: <FileTextOutlined /> },
        { key: '/growth/reports/delivery-note-register', label: 'Delivery note register', icon: <FileTextOutlined /> },
        { key: '/growth/reports/customer-analysis', label: 'Customer analysis', icon: <BarChartOutlined /> },
        { key: '/growth/reports/invoice-aging', label: 'Invoice aging', icon: <BarChartOutlined /> },
        { key: '/growth/reports/quotation-aging', label: 'Quotation aging', icon: <BarChartOutlined /> },
        { key: '/growth/reports/opportunity-aging', label: 'Opportunity aging', icon: <BarChartOutlined /> },
        { key: '/growth/reports/credit-note-register', label: 'Credit note register', icon: <FileTextOutlined /> },
        { key: '/growth/reports/opportunity-register', label: 'Opportunity register', icon: <FileTextOutlined /> },
        { key: '/growth/reports/opportunity-to-fulfillment', label: 'Enquiry & Contract-Tender Register', icon: <FileTextOutlined /> },
        { key: '/growth/reports/quotation-list-register', label: 'Quotation register', icon: <FileTextOutlined /> },
        { key: '/growth/quotation-conversion-reports', label: 'Quotation Conversion', icon: <BarChartOutlined /> },
        { key: '/growth/reports/tre-overhead', label: 'TRE overhead / margin', icon: <BarChartOutlined /> },
        { key: '/growth/reports/project-costing', label: 'Project costing', icon: <BarChartOutlined /> },
      ],
    },

    scm: {
      label: 'Supply Chain',
      items: [
        { type: 'group', label: 'Workboard' },
        { key: '/scm/dashboard', label: 'Supply Chain Dashboard', icon: <DashboardOutlined /> },
        { type: 'group', label: 'Sourcing' },
        { key: '/scm/procurement/suppliers', label: 'Suppliers', icon: <ShopOutlined /> },
        { key: '/scm/procurement/control-tower', label: 'Procurement tower', icon: <DashboardOutlined /> },
        { key: '/scm/procurement/supplier-categories', label: 'Supplier categories', icon: <TagsOutlined /> },
        { key: '/scm/procurement/purchase-requests', label: 'Purchase requests', icon: <FileTextOutlined /> },
        { key: '/scm/procurement/spot-purchase', label: 'Spot / Market Purchase', icon: <ShoppingCartOutlined /> },
        { key: '/scm/procurement/supplier-inquiries', label: 'Supplier inquiries', icon: <FileTextOutlined /> },
        { key: '/scm/procurement/supplier-quotations', label: 'Supplier quotations', icon: <FileTextOutlined /> },
        { key: '/scm/procurement/bid-analyses', label: 'Bid analyses', icon: <FileTextOutlined /> },
        { type: 'group', label: 'Transactions' },
        { key: '/scm/procurement/purchase-orders', label: 'Purchase orders', icon: <ShoppingCartOutlined /> },
        { key: '/scm/procurement/grn', label: 'Goods receipt (GRN)', icon: <InboxOutlined /> },
        { key: '/scm/procurement/receiving-inspections', label: 'Receiving Inspections', icon: <InboxOutlined /> },
        { key: '/scm/procurement/service-receipts', label: 'Service receipts', icon: <InboxOutlined /> },
        { key: '/scm/procurement/supplier-invoices', label: 'Supplier invoices', icon: <FileTextOutlined /> },
        { key: '/scm/procurement/three-way-matching', label: 'Three-way matching', icon: <FileTextOutlined /> },
        { key: '/scm/procurement/purchase-returns', label: 'Purchase returns', icon: <InboxOutlined /> },
        { type: 'group', label: 'Warehouse' },
        { key: '/scm/warehouse/warehouses', label: 'Warehouses', icon: <HomeOutlined /> },
        { key: '/scm/warehouse/stock-transfer', label: 'Stock transfer', icon: <SwapOutlined /> },
        { key: '/scm/warehouse/stock-adjustment', label: 'Stock adjustment', icon: <EditOutlined /> },
        { type: 'group', label: 'Inventory' },
        { key: '/scm/inventory/items', label: 'Items', icon: <AppstoreOutlined /> },
        { key: '/scm/inventory/item-categories', label: 'Item categories', icon: <FolderOutlined /> },
        { key: '/scm/inventory/uom-units', label: 'Units of measure (UOM)', icon: <AppstoreOutlined /> },
        { key: '/scm/inventory/engineering', label: 'Engineering materials & weights', icon: <AppstoreOutlined /> },
        { key: '/inventory/item-classification-gl-accounts', label: 'Item classification GL', icon: <AccountBookOutlined /> },
        { key: '/scm/inventory/material-requests', label: 'Material requests', icon: <FileTextOutlined /> },
        { key: '/scm/inventory/material-issues', label: 'Material issues', icon: <ExportOutlined /> },
        { key: '/scm/inventory/material-returns', label: 'Material returns', icon: <InboxOutlined /> },
        { key: '/scm/inventory/site-materials', label: 'Project site materials', icon: <HomeOutlined /> },
        { key: '/scm/inventory/stock-summary', label: 'Stock on hand', icon: <BarChartOutlined /> },
        { key: '/scm/inventory/heat-stock', label: 'Heat-wise stock', icon: <BarChartOutlined /> },
        { key: '/scm/inventory/stock-count', label: 'Stock count', icon: <EditOutlined /> },
        { key: '/scm/inventory/putaway', label: 'Bin putaway', icon: <InboxOutlined /> },
        { key: '/scm/inventory/consignment-receipts', label: 'Consignment receipts', icon: <InboxOutlined /> },
        { key: '/scm/inventory/consignment-returns', label: 'Consignment returns', icon: <ExportOutlined /> },
        { key: '/scm/inventory/opening-stock', label: 'Opening stock upload', icon: <UploadOutlined /> },
        { type: 'group', label: 'Supplier quality' },
        { key: '/scm/quality/supplier-assessments', label: 'Supplier assessments', icon: <FileTextOutlined /> },
        { key: '/scm/quality/performance-monitoring', label: 'Performance monitoring', icon: <BarChartOutlined /> },
        { key: '/scm/quality/receiving-verification', label: 'Receiving verification', icon: <CheckCircleOutlined /> },
        { key: '/scm/quality/critical-mtc', label: 'Critical MTC gaps', icon: <FileTextOutlined /> },
        { key: '/scm/quality/outsourcing', label: 'Outsourcing control', icon: <BranchesOutlined /> },
        { type: 'group', label: 'Reports' },
        { key: '/scm/reports', label: 'SCM reports hub', icon: <BarChartOutlined /> },
        { key: '/scm/reports/purchase-register', label: 'Purchase register', icon: <FileTextOutlined /> },
        { key: '/scm/reports/grn-register', label: 'Goods receipt register', icon: <FileTextOutlined /> },
        { key: '/scm/reports/supplier-invoice-register', label: 'Supplier invoice register', icon: <FileTextOutlined /> },
        { key: '/scm/reports/po-aging', label: 'PO aging', icon: <BarChartOutlined /> },
        { key: '/scm/reports/supplier-analysis', label: 'Supplier analysis', icon: <BarChartOutlined /> },
        { key: '/scm/procurement/reports/po-milestones', label: 'PO milestones', icon: <BarChartOutlined /> },
        { key: '/scm/reports/inventory-valuation', label: 'Inventory valuation', icon: <BarChartOutlined /> },
        { key: '/scm/reports/gl-reconciliation', label: 'Inventory ↔ GL recon', icon: <BarChartOutlined /> },
        {
          key: '/scm/reports/project-material-reconciliation',
          label: 'Project material recon',
          icon: <BarChartOutlined />,
        },
        { key: '/scm/reports/stock-aging', label: 'Stock aging', icon: <BarChartOutlined /> },
        { key: '/scm/reports/abc-analysis', label: 'ABC analysis', icon: <BarChartOutlined /> },
        { key: '/scm/reports/dead-stock', label: 'Dead stock', icon: <BarChartOutlined /> },
        { key: '/scm/reports/purchase-price-variance', label: 'PPV register', icon: <BarChartOutlined /> },
        { key: '/scm/reports/demand-planning', label: 'Demand planning', icon: <BarChartOutlined /> },
        { key: '/scm/reports/landed-cost-revalue', label: 'Landed cost revalue', icon: <BarChartOutlined /> },
      ],
    },

    mfg: {
      label: 'Manufacturing',
      items: [
        { type: 'group', label: 'Production Control' },
        { key: '/mfg/control-tower', label: 'Control Tower', icon: <DashboardOutlined /> },
        { key: '/mfg/factory-floor', label: 'Factory floor display', icon: <DesktopOutlined /> },
        { key: '/mfg/workstation-control-center', label: 'Workstation Control Center', icon: <DesktopOutlined /> },
        { key: '/mfg/production-requests', label: 'Production requests', icon: <FileTextOutlined /> },
        { key: '/mfg/work-orders', label: 'Work Orders', icon: <ToolOutlined /> },
        { key: '/mfg/work-order-operations', label: 'WO operations', icon: <PlayCircleOutlined /> },
        { key: '/mfg/work-order-operations/eod-close', label: 'End of day close', icon: <CheckCircleOutlined /> },
        { key: '/mfg/traceability-matrix', label: 'Traceability matrix', icon: <TableOutlined /> },
        { type: 'group', label: 'Engineering' },
        { key: '/mfg/boms', label: 'Bill of Materials', icon: <FileTextOutlined /> },
        { key: '/mfg/workstations', label: 'Workstations', icon: <DesktopOutlined /> },
        { type: 'group', label: 'Planning' },
        { key: '/mfg/calendar', label: 'Production Calendar', icon: <CalendarOutlined /> },
        { key: '/mfg/capacity-planning', label: 'Capacity Planning', icon: <BarChartOutlined /> },
        { key: '/mfg/master-schedule', label: 'Master Schedule', icon: <BarChartOutlined /> },
        { key: '/mfg/material-audit', label: 'Material Audit', icon: <BarChartOutlined /> },
        { type: 'group', label: 'Reports' },
        { key: '/mfg/reports', label: 'MFG reports hub', icon: <BarChartOutlined /> },
        { key: '/mfg/reports/work-order-register', label: 'Work order register', icon: <FileTextOutlined /> },
        { key: '/mfg/reports/operations-status', label: 'Operations status', icon: <FileTextOutlined /> },
        { key: '/mfg/route-sheet-register', label: 'Route sheet register', icon: <FileTextOutlined /> },
        { key: '/mfg/reports/outsourced-fabrication', label: 'Outsourced fabrication', icon: <BarChartOutlined /> },
      ],
    },

    asset: {
      label: 'Asset Management',
      items: [
        { type: 'group', label: 'Register' },
        { key: '/asset/assets', label: 'Assets', icon: <GoldOutlined /> },
        { type: 'group', label: 'Tracking' },
        { key: '/asset/scan', label: 'Scan & find', icon: <QrcodeOutlined /> },
        { type: 'group', label: 'Operations' },
        { key: '/asset/maintenance', label: 'Maintenance (PM/CM/AMC)', icon: <ToolOutlined /> },
        { type: 'group', label: 'Workshop / Garage' },
        { key: '/asset/workshop', label: 'Workshop Operations', icon: <ToolOutlined /> },
        { type: 'group', label: 'Plant, Machinery & Vehicles' },
        { key: '/asset/pmv/hub', label: 'PMV Hub', icon: <DashboardOutlined /> },
        { key: '/asset/pmv/control-tower', label: 'Command Center', icon: <DashboardOutlined /> },
        { key: '/asset/pmv', label: 'Fleet Register', icon: <GoldOutlined /> },
        { key: '/asset/pmv/operations', label: 'Deployment Control', icon: <ToolOutlined /> },
        { key: '/asset/pmv/requests', label: 'Equipment Requests', icon: <FileTextOutlined /> },
        { type: 'group', label: 'Field Operations' },
        { key: '/asset/pmv/daily-log-sheets', label: 'Daily Log Sheets (Bulk)', icon: <CalendarOutlined /> },
        { key: '/asset/pmv/daily-logs', label: 'Daily Logs', icon: <CalendarOutlined /> },
        { key: '/asset/pmv/daily-log-sheets/bulk', label: 'Bulk Entry', icon: <FileTextOutlined /> },
        {
          key: '/asset/pmv/daily-log-sheets/exceptions',
          label: 'Field Exceptions',
          icon: <WarningOutlined />,
        },
        { type: 'group', label: 'Hired / Rented Equipment' },
        { key: '/asset/pmv/hire/control', label: 'Hire Control', icon: <DashboardOutlined /> },
        { key: '/asset/pmv/hire/agreements', label: 'Hire Agreements', icon: <FileTextOutlined /> },
        { key: '/asset/pmv/hire', label: 'On-Hire Equipment', icon: <GoldOutlined /> },
        { key: '/asset/pmv/hire/on-off', label: 'On / Off-Hire', icon: <ToolOutlined /> },
        { key: '/asset/pmv/hire/timesheets', label: 'Hire Timesheets', icon: <CalendarOutlined /> },
        { key: '/asset/pmv/hire/statements', label: 'Hire Statements', icon: <FileTextOutlined /> },
        {
          key: '/asset/pmv/hire/reconciliations',
          label: 'Invoice Reconciliation',
          icon: <AuditOutlined />,
        },
        {
          key: '/asset/pmv/hire/attention',
          label: 'Commercial Attention',
          icon: <WarningOutlined />,
        },
        { type: 'group', label: 'Components & Fuel' },
        { key: '/asset/pmv/components', label: 'Components', icon: <ToolOutlined /> },
        {
          key: '/asset/pmv/components/control-tower',
          label: 'Component Tower',
          icon: <DashboardOutlined />,
        },
        { key: '/asset/pmv/fuel/control-tower', label: 'Fuel Control Tower', icon: <DashboardOutlined /> },
        { key: '/asset/pmv/fuel', label: 'Fuel Register', icon: <BarChartOutlined /> },
        { key: '/asset/pmv/fuel/reconciliation', label: 'Fuel Reconciliation', icon: <AuditOutlined /> },
        { key: '/asset/pmv/fuel/attention', label: 'Fuel Attention', icon: <WarningOutlined /> },
        { key: '/asset/pmv/fuel/sources', label: 'Fuel Sources', icon: <GoldOutlined /> },
        { type: 'group', label: 'Reliability' },
        {
          key: '/asset/pmv/reliability/control-tower',
          label: 'Reliability Tower',
          icon: <DashboardOutlined />,
        },
        { key: '/asset/pmv/reliability', label: 'Health Register', icon: <BarChartOutlined /> },
        {
          key: '/asset/pmv/reliability/attention',
          label: 'Reliability Attention',
          icon: <WarningOutlined />,
        },
        {
          key: '/asset/pmv/reliability/failures',
          label: 'Failures',
          icon: <FileTextOutlined />,
        },
        { key: '/asset/pmv/reliability/faults', label: 'Fault Codes', icon: <ApiOutlined /> },
        {
          key: '/asset/pmv/reliability/predictions',
          label: 'Predictions',
          icon: <BarChartOutlined />,
        },
        {
          key: '/asset/pmv/reliability/forecast',
          label: 'Maintenance Forecast',
          icon: <CalendarOutlined />,
        },
        {
          key: '/asset/pmv/reliability/conditions',
          label: 'Condition Monitoring',
          icon: <BarChartOutlined />,
        },
        { type: 'group', label: 'Connected Fleet' },
        { key: '/asset/pmv/connected', label: 'Connected Fleet', icon: <ApiOutlined /> },
        { key: '/asset/pmv/connected/map', label: 'Live Fleet Map', icon: <ApiOutlined /> },
        { key: '/asset/pmv/connected/movement-attention', label: 'Spatial attention', icon: <ApiOutlined /> },
        { key: '/asset/pmv/connected/geofences', label: 'Geofences', icon: <ApiOutlined /> },
        { key: '/asset/pmv/setup', label: 'Configuration', icon: <SettingOutlined /> },
        { key: '/asset/depreciation', label: 'Depreciation', icon: <GoldOutlined /> },
        { key: '/asset/disposal', label: 'Disposal', icon: <MinusCircleOutlined /> },
        { key: '/asset/accounting-defaults', label: 'Accounting defaults', icon: <SettingOutlined /> },
        { key: '/asset/groups', label: 'Asset groups', icon: <SettingOutlined /> },
        { key: '/asset/insurance', label: 'Insurance', icon: <SafetyCertificateOutlined /> },
        { key: '/asset/audit', label: 'Audit & verification', icon: <AuditOutlined /> },
        { key: '/asset/audit/report', label: 'Audit report (summary)', icon: <FileTextOutlined /> },
        { type: 'group', label: 'Reports' },
        { key: '/asset/reports', label: 'Asset reports hub', icon: <BarChartOutlined /> },
        { key: '/asset/reports/movements', label: 'Movement log', icon: <FileTextOutlined /> },
        { key: '/asset/reports/location', label: 'Location report', icon: <HomeOutlined /> },
      ],
    },

    fin: {
      label: 'Finance',
      items: [
        { type: 'group', label: 'Workboard' },
        { key: '/fin/dashboard', label: 'Finance Dashboard', icon: <DashboardOutlined /> },
        { type: 'group', label: 'General ledger & vouchers' },
        { key: '/fin/gl/chart-of-accounts', label: 'Chart of accounts', icon: <AccountBookOutlined /> },
        { key: '/fin/gl/journals', label: 'Journal vouchers', icon: <FileTextOutlined /> },
        { key: '/fin/proforma-invoices', label: 'Proforma invoices', icon: <FileTextOutlined /> },
        { key: '/fin/receipt-vouchers', label: 'Receipt vouchers', icon: <FileTextOutlined /> },
        { key: '/fin/payment-vouchers', label: 'Payment vouchers', icon: <FileTextOutlined /> },
        { type: 'group', label: 'Accounts payable' },
        { key: '/finance/ap/supplier-advance-refunds', label: 'Supplier advance refunds', icon: <ReconciliationOutlined /> },
        { key: '/fin/direct-purchase-invoices', label: 'Direct purchase invoices', icon: <FileTextOutlined /> },
        { key: '/fin/prepaid', label: 'Prepaid expenses', icon: <ReconciliationOutlined /> },
        { key: '/fin/debit-notes', label: 'Debit notes', icon: <FileTextOutlined /> },
        { key: '/fin/credit-notes', label: 'Credit notes', icon: <FileTextOutlined /> },
        { key: '/fin/currencies', label: 'Currencies', icon: <DollarOutlined /> },
        { type: 'group', label: 'Intercompany' },
        { key: '/fin/intercompany/affiliates', label: 'Affiliate companies', icon: <LinkOutlined /> },
        { key: '/fin/intercompany/transactions', label: 'IC transactions', icon: <SwapOutlined /> },
        { key: '/fin/intercompany/settlements', label: 'IC settlements', icon: <ReconciliationOutlined /> },
        { key: '/fin/intercompany/groups', label: 'Consolidation groups', icon: <ApartmentOutlined /> },
        { type: 'group', label: 'Tax' },
        { key: '/fin/tax/vat-return', label: 'UAE VAT return', icon: <CalculatorOutlined /> },
        { type: 'group', label: 'Masters' },
        { key: '/fin/departments', label: 'Departments', icon: <TableOutlined /> },
        { key: '/fin/payment-terms', label: 'Payment terms', icon: <FileTextOutlined /> },
        { key: '/fin/banking/accounts', label: 'Bank accounts', icon: <BankOutlined /> },
        { key: '/platform/foundation/revenue-service-types', label: 'Revenue service types', icon: <GoldOutlined /> },
        { type: 'group', label: 'Reports' },
        { key: '/fin/reports', label: 'Financial reports hub', icon: <BarChartOutlined /> },
        { key: '/fin/reports/monthly-profit-loss', label: 'Monthly P&L', icon: <BarChartOutlined /> },
        { key: '/fin/reports/operational-profit', label: 'Operational profit', icon: <BarChartOutlined /> },
        { key: '/fin/reports/ledger', label: 'Ledger', icon: <AccountBookOutlined /> },
        { key: '/fin/reports/project-ledger', label: 'Project ledger', icon: <AccountBookOutlined /> },
        { key: '/fin/reports/project-profit-loss', label: 'Project P&L', icon: <BarChartOutlined /> },
        { key: '/fin/reports/customer-outstanding', label: 'Customer outstanding', icon: <BarChartOutlined /> },
        { key: '/fin/reports/supplier-outstanding', label: 'Supplier outstanding', icon: <BarChartOutlined /> },
        { key: '/fin/reports/customer-statement', label: 'Customer statement', icon: <FileTextOutlined /> },
        { key: '/fin/reports/supplier-statement', label: 'Supplier statement', icon: <FileTextOutlined /> },
        { key: '/fin/reports/settlement-register', label: 'Settlement register', icon: <FileTextOutlined /> },
        { key: '/fin/reports/payment-register', label: 'Payment register', icon: <FileTextOutlined /> },
        { key: '/fin/reports/receipt-register', label: 'Receipt register', icon: <FileTextOutlined /> },
        { key: '/fin/reports/direct-purchase-invoice-register', label: 'Direct PI register', icon: <FileTextOutlined /> },
        { key: '/fin/reports/supplier-advance-register', label: 'Supplier advance register', icon: <FileTextOutlined /> },
        { key: '/fin/reports/intercompany-balance', label: 'IC balance report', icon: <BarChartOutlined /> },
        { key: '/fin/reports/intercompany-statement', label: 'IC statement', icon: <FileTextOutlined /> },
        { key: '/fin/reports/intercompany-consolidation', label: 'Group consolidation', icon: <BarChartOutlined /> },
      ],
    },

    qms: {
      label: 'Quality & Compliance',
      items: [
        { type: 'group', label: 'Quality Management' },
        { key: '/qms/dashboard', label: 'Quality Dashboard', icon: <DashboardOutlined /> },
        { key: '/qms/quality-plans', label: 'Quality Plans', icon: <FileProtectOutlined /> },
        { key: '/qms/inspections', label: 'Stage Inspections', icon: <SearchOutlined /> },
        {
          key: '/qms/receiving-dimensional-inspections',
          label: 'Receiving Dimension',
          icon: <SearchOutlined />,
        },
        { key: '/qms/initial-inspection-requests', label: 'Initial Inspection', icon: <SearchOutlined /> },
        { key: '/qms/final-inspection-reports', label: 'Final Inspection', icon: <SearchOutlined /> },
        { key: '/qms/inspection-release-notes', label: 'Inspection release notes', icon: <FileProtectOutlined /> },
        { key: '/qms/certificates-of-conformity', label: 'Certificate of Conformity', icon: <FileProtectOutlined /> },
        { key: '/qms/tmmde', label: 'Equipment List', icon: <ToolOutlined /> },
        { key: '/qms/dimensional-inspection', label: 'Dimensional Inspection', icon: <SearchOutlined /> },
        { key: '/qms/ncrs', label: 'NCRs', icon: <WarningOutlined /> },
        { key: '/qms/capas', label: 'CAPAs', icon: <SolutionOutlined /> },
        { key: '/qms/audits', label: 'Audits', icon: <AuditOutlined /> },
        { type: 'group', label: 'Risk & Control' },
        { key: '/grc/risk-assessments', label: 'Risk Assessments', icon: <WarningOutlined /> },
        { key: '/grc/moc', label: 'Management of Change (MOC)', icon: <FileProtectOutlined /> },
        { key: '/grc/calibration', label: 'Calibration', icon: <AuditOutlined /> },
        { type: 'group', label: 'Competency & Training' },
        { key: '/grc/competency', label: 'Competency & Training', icon: <UserOutlined /> },
        { key: '/grc/competency/schedule', label: 'Annual training schedule', icon: <SolutionOutlined /> },
        { key: '/grc/competency/sessions', label: 'Training attendance', icon: <SolutionOutlined /> },
        { key: '/grc/competency/assessments', label: 'Competency assessments', icon: <AuditOutlined /> },
        { type: 'group', label: 'Regional Compliance' },
        { key: '/compliance/uae-einvoicing', label: 'UAE E-Invoicing', icon: <FileTextOutlined /> },
        { key: '/compliance/inbound-einvoices', label: 'Inbound e-invoices', icon: <FileTextOutlined /> },
        { key: '/compliance/ksa-zatca', label: 'KSA ZATCA', icon: <SafetyCertificateOutlined /> },
        { type: 'group', label: 'Reports' },
        { key: '/qms/reports', label: 'QMS reports hub', icon: <BarChartOutlined /> },
        { key: '/grc/moc/reports', label: 'MOC reports', icon: <BarChartOutlined /> },
      ],
    },

    prj: {
      label: 'Projects',
      items: [
        { type: 'group', label: 'Project Execution' },
        { key: '/prj/projects', label: 'Projects', icon: <ProjectOutlined /> },
        { key: '/prj/budgets', label: 'Project Cost Budget', icon: <AccountBookOutlined /> },
        { key: '/prj/timesheets', label: 'Timesheets', icon: <ClockCircleOutlined /> },
        { key: '/prj/costs', label: 'Project Costs', icon: <DollarOutlined /> },
        { type: 'group', label: 'Reports' },
        { key: '/prj/reports', label: 'Project reports hub', icon: <BarChartOutlined /> },
        { key: '/prj/reports/financial-summary', label: 'Financial summary', icon: <FileTextOutlined /> },
        { key: '/prj/project-health', label: 'Project health hub', icon: <LineChartOutlined /> },
        { key: '/projects/reports', label: 'Project health reports', icon: <BarChartOutlined /> },
        { key: '/prj/dashboard', label: 'Project Dashboard', icon: <BarChartOutlined /> },
      ],
    },

    epc: {
      label: 'EPC',
      items: [
        { type: 'group', label: 'EPC Operating System' },
        { key: '/epc', label: 'EPC Hub', icon: <ProjectOutlined /> },
        { key: '/epc/libraries/wbs', label: 'WBS Libraries', icon: <ApartmentOutlined /> },
        { type: 'group', label: 'Estimating Libraries' },
        { key: '/epc/libraries/labour', label: 'Labour rate book', icon: <TeamOutlined /> },
        { key: '/epc/libraries/equipment', label: 'Equipment rate book', icon: <DesktopOutlined /> },
        { key: '/epc/libraries/crews', label: 'Crews', icon: <TeamOutlined /> },
        { key: '/epc/libraries/norms', label: 'Productivity norms', icon: <LineChartOutlined /> },
        { key: '/epc/libraries/assemblies', label: 'Assemblies', icon: <ApartmentOutlined /> },
        {
          key: '/epc/libraries/rate-resolver',
          label: 'Rate Resolver',
          icon: <CalculatorOutlined />,
        },
        { type: 'group', label: 'Commercial' },
        { key: '/epc/commercial', label: 'Commercial Hub', icon: <DollarOutlined /> },
        {
          key: '/epc/commercial/prequalifications?kind=EOI',
          label: 'Expression of Interest (EOI)',
          icon: <FileProtectOutlined />,
        },
        {
          key: '/epc/commercial/tenders?kind=RFQ',
          label: 'Request for Quotation (RFQ)',
          icon: <FileTextOutlined />,
        },
        {
          key: '/epc/commercial/tenders?kind=RFT',
          label: 'Request for Tender (RFT)',
          icon: <FileTextOutlined />,
        },
        { key: '/epc/commercial/prequalifications', label: 'Prequalifications', icon: <FileProtectOutlined /> },
        { key: '/epc/commercial/tenders', label: 'Tender Register', icon: <FileTextOutlined /> },
        {
          key: '/epc/commercial/tenders?intent=estimate',
          label: 'Tender Estimation',
          icon: <CalculatorOutlined />,
        },
        { key: '/epc/commercial/bid-decisions', label: 'Bid Decisions', icon: <SolutionOutlined /> },
        { key: '/epc/commercial/awards', label: 'Awards', icon: <TrophyOutlined /> },
        { key: '/epc/commercial/contracts', label: 'Contracts', icon: <FileProtectOutlined /> },
        { key: '/epc/commercial/subcontracts', label: 'Subcontracts', icon: <FileProtectOutlined /> },
        { key: '/epc/commercial/mobilizations', label: 'Mobilizations', icon: <RocketOutlined /> },
        { key: '/epc/commercial/control-tower', label: 'Commercial Control', icon: <DashboardOutlined /> },
        { type: 'group', label: 'Payment Applications (IPC)' },
        { key: '/epc/ipc', label: 'IPC register', icon: <FileTextOutlined /> },
        { key: '/epc/ipc/certifications', label: 'Certifications', icon: <FileProtectOutlined /> },
        { type: 'group', label: 'Construction Execution' },
        { key: '/epc/execution', label: 'Construction Hub', icon: <DashboardOutlined /> },
        {
          key: '/epc/execution?tab=execution-packages',
          label: 'Execution Packages',
          icon: <ToolOutlined />,
        },
        {
          key: '/epc/execution?tab=construction-areas',
          label: 'Construction Areas',
          icon: <AppstoreOutlined />,
        },
        {
          key: '/epc/execution?tab=work-fronts',
          label: 'Work Fronts',
          icon: <BranchesOutlined />,
        },
        { key: '/epc/execution?tab=cep-progress', label: 'Progress', icon: <BarChartOutlined /> },
        {
          key: '/epc/execution?tab=constraints',
          label: 'Constraints',
          icon: <WarningOutlined />,
        },
        { key: '/epc/execution?tab=manpower', label: 'Manpower', icon: <TeamOutlined /> },
        { key: '/epc/execution?tab=equipment', label: 'Equipment', icon: <DesktopOutlined /> },
        { key: '/epc/execution?tab=permits', label: 'Permits', icon: <FileProtectOutlined /> },
        {
          key: '/epc/execution?tab=productivity',
          label: 'Productivity',
          icon: <LineChartOutlined />,
        },
        {
          key: '/epc/execution?tab=dashboards',
          label: 'Dashboards',
          icon: <DashboardOutlined />,
        },
        { type: 'group', label: 'Cost Budget & Control' },
        { key: '/prj/budgets', label: 'Project Cost Budget', icon: <AccountBookOutlined /> },
        {
          key: '/prj/projects',
          label: 'Projects (budget / BVA)',
          icon: <ProjectOutlined />,
        },
        { key: '/prj/costs', label: 'Project Costs', icon: <DollarOutlined /> },
        { key: '/prj/timesheets', label: 'Timesheets', icon: <ClockCircleOutlined /> },
        { type: 'group', label: 'Reports' },
        { key: '/epc/reports', label: 'EPC reports hub', icon: <BarChartOutlined /> },
        { key: '/epc/reports/ipc-register', label: 'IPC register', icon: <FileTextOutlined /> },
        { key: '/epc/reports/sca-register', label: 'SCA register', icon: <FileTextOutlined /> },
      ],
    },

    hr: {
      label: 'HRMS',
      items: [
        { type: 'group', label: 'My HRMS' },
        { key: '/hr/me', label: 'My HR home', icon: <UserOutlined /> },
        { key: '/hr/me/leave', label: 'My leave', icon: <CalendarOutlined /> },
        { key: '/hr/me/ot', label: 'My overtime', icon: <ClockCircleOutlined /> },
        { key: '/hr/me/payslips', label: 'My payslips', icon: <DollarOutlined /> },
        { key: '/hr/me/profile', label: 'My profile', icon: <IdcardOutlined /> },
        { key: '/hr/me/letters', label: 'Letter requests', icon: <FileTextOutlined /> },
        { key: '/hr/me/timesheets', label: 'My timesheets', icon: <CalendarOutlined /> },
        { type: 'group', label: 'Setup' },
        { key: '/hr/setup', label: 'HRMS Setup', icon: <SettingOutlined /> },
        { key: '/hr/accruals', label: 'Accrual Management', icon: <CalculatorOutlined /> },
        { key: '/hr/holidays', label: 'Holidays', icon: <CalendarOutlined /> },
        { key: '/hr/shifts', label: 'Shifts', icon: <ClockCircleOutlined /> },
        { key: '/hr/locations', label: 'Locations', icon: <ProjectOutlined /> },
        { key: '/hr/positions', label: 'Positions', icon: <TeamOutlined /> },
        { key: '/hr/grades', label: 'Grades', icon: <TeamOutlined /> },
        { key: '/hr/salary-heads', label: 'Salary heads', icon: <DollarOutlined /> },
        { key: '/hr/leave-types', label: 'Leave types', icon: <CalendarOutlined /> },
        { key: '/hr/leave-opening-balances', label: 'Leave openings', icon: <CalendarOutlined /> },
        { type: 'group', label: 'Workforce' },
        { key: '/hr/employees', label: 'Employees', icon: <TeamOutlined /> },
        { key: '/hr/employees/location-assignment', label: 'Location assignment', icon: <ProjectOutlined /> },
        { key: '/hr/employees/shift-assignment', label: 'Shift assignment', icon: <ClockCircleOutlined /> },
        { key: '/hr/contracts', label: 'Contracts', icon: <FileTextOutlined /> },
        { key: '/hr/profile-change-requests', label: 'Profile requests', icon: <SolutionOutlined /> },
        { key: '/hr/letter-requests', label: 'Letter requests (HR)', icon: <FileTextOutlined /> },
        { key: '/hr/allocations', label: 'Project allocations', icon: <ProjectOutlined /> },
        { key: '/hr/asset-custody', label: 'Asset custody', icon: <InboxOutlined /> },
        { type: 'group', label: 'Talent Acquisition' },
        { key: '/hr/recruitment', label: 'TA home', icon: <DashboardOutlined /> },
        { key: '/hr/interviews/mine', label: 'My interviews', icon: <CalendarOutlined /> },
        { key: '/hr/onboarding/my-tasks', label: 'My onboarding tasks', icon: <UserOutlined /> },
        { type: 'group', label: 'TA · Hiring' },
        { key: '/hr/requisitions', label: 'Requisitions', icon: <SolutionOutlined /> },
        { key: '/hr/jobs', label: 'Job postings', icon: <FileTextOutlined /> },
        { key: '/hr/candidates', label: 'Candidates', icon: <TeamOutlined /> },
        { type: 'group', label: 'TA · Pipeline' },
        { key: '/hr/applications', label: 'Applications', icon: <SolutionOutlined /> },
        { key: '/hr/interviews', label: 'Interviews', icon: <CalendarOutlined /> },
        { key: '/hr/assessments', label: 'Assessments', icon: <SolutionOutlined /> },
        { type: 'group', label: 'TA · Offers & onboarding' },
        { key: '/hr/offers', label: 'Offers', icon: <FileTextOutlined /> },
        { key: '/hr/onboarding', label: 'Onboarding', icon: <UserOutlined /> },
        { type: 'group', label: 'TA · Setup & reports' },
        { key: '/hr/careers-settings', label: 'Recruitment setup', icon: <SettingOutlined /> },
        { key: '/hr/recruit-reports', label: 'Recruitment reports', icon: <BarChartOutlined /> },
        { type: 'group', label: 'Time & Attendance' },
        { key: '/hr/attendance', label: 'Attendance / Timesheets', icon: <CalendarOutlined /> },
        { key: '/hr/overtime', label: 'Overtime', icon: <ClockCircleOutlined /> },
        { key: '/hr/leaves', label: 'Leaves', icon: <CalendarOutlined /> },
        { key: '/hr/leave-settlements', label: 'Leave settlements', icon: <DollarOutlined /> },
        { type: 'group', label: 'Payroll' },
        { key: '/hr/adjustments', label: 'Additions / Deductions', icon: <DollarOutlined /> },
        { key: '/hr/loans', label: 'Loans', icon: <DollarOutlined /> },
        { key: '/hr/salary-revisions', label: 'Salary revisions', icon: <DollarOutlined /> },
        { key: '/hr/payroll', label: 'Payroll', icon: <DollarOutlined /> },
        { key: '/hr/eosb', label: 'EOSB', icon: <SolutionOutlined /> },
        { type: 'group', label: 'Documents' },
        { key: '/hr/documents', label: 'Document library', icon: <FileTextOutlined /> },
        { key: '/hr/document-expiry', label: 'Document expiry', icon: <FileTextOutlined /> },
        { key: '/hr/audit-log', label: 'Audit log', icon: <BarChartOutlined /> },
        { type: 'group', label: 'Reports' },
        { key: '/hr/reports', label: 'HRMS reports hub', icon: <BarChartOutlined /> },
        { key: '/hr/reports/headcount', label: 'Headcount', icon: <TeamOutlined /> },
        { key: '/hr/reports/attendance', label: 'Attendance', icon: <CalendarOutlined /> },
        { key: '/hr/reports/leave-liability', label: 'Leave liability', icon: <CalendarOutlined /> },
        { key: '/hr/reports/ot', label: 'Overtime', icon: <ClockCircleOutlined /> },
        { key: '/hr/reports/payroll-register', label: 'Payroll register', icon: <DollarOutlined /> },
        { key: '/hr/reports/loans', label: 'Loans', icon: <DollarOutlined /> },
        { key: '/hr/reports/document-expiry', label: 'Document expiry', icon: <FileTextOutlined /> },
        { key: '/hr/reports/eosb', label: 'EOSB', icon: <SolutionOutlined /> },
        { key: '/hr/reports/allocations', label: 'Allocations', icon: <ProjectOutlined /> },
        { key: '/hr/reports/asset-custody', label: 'Asset custody', icon: <InboxOutlined /> },
      ],
    },

    analytics: {
      label: 'Analytics',
      items: [
        { type: 'group', label: 'Executive' },
        { key: '/analytics/executive', label: 'Company KPI Dashboard', icon: <DashboardOutlined /> },
        { type: 'group', label: 'Sales Analytics' },
        { key: '/growth/dashboard?tab=analytics', label: 'Sales Analytics', icon: <LineChartOutlined /> },
        { type: 'group', label: 'Supply Chain Analytics' },
        { key: '/analytics/scm', label: 'SCM Analytics', icon: <BarChartOutlined /> },
        { type: 'group', label: 'Manufacturing Analytics' },
        { key: '/analytics/mfg', label: 'MFG Analytics', icon: <LineChartOutlined /> },
        { type: 'group', label: 'Quality Analytics' },
        { key: '/analytics/quality', label: 'Quality Analytics', icon: <BarChartOutlined /> },
        { type: 'group', label: 'ERP AI' },
        { key: '/ai/workspace', label: 'ERP AI Workspace', icon: <RobotOutlined /> },
        { key: '/ai/udq', label: 'AI UDQ Builder', icon: <RobotOutlined /> },
        { key: '/ai/dashboard', label: 'My AI Dashboard', icon: <DashboardOutlined /> },
        { key: '/ai/enterprise-memory', label: 'Enterprise Memory', icon: <RobotOutlined /> },
        { type: 'group', label: 'Reports' },
        { key: '/reports', label: 'All reports', icon: <BarChartOutlined /> },
      ],
    },

    ownerIntel: {
      label: 'Owner Intelligence',
      items: [
        { type: 'group', label: 'Command' },
        { key: '/owner-intelligence', label: 'Command Center', icon: <DashboardOutlined /> },
        { key: '/home/exceptions', label: 'Management Exceptions', icon: <WarningOutlined /> },
        { key: '/owner-intelligence/group', label: 'Group GM', icon: <BankOutlined /> },
        { type: 'group', label: 'Health' },
        { key: '/owner-intelligence/business-health', label: 'Business Health', icon: <SafetyCertificateOutlined /> },
        { key: '/owner-intelligence/financial-health', label: 'Financial Health', icon: <AccountBookOutlined /> },
        { key: '/owner-intelligence/kpis', label: 'KPI Performance', icon: <BarChartOutlined /> },
        { type: 'group', label: 'Growth' },
        { key: '/owner-intelligence/profit', label: 'Revenue & Profit', icon: <LineChartOutlined /> },
        { key: '/owner-intelligence/cash', label: 'Cash & WC', icon: <DollarOutlined /> },
        { key: '/owner-intelligence/customers', label: 'Customers', icon: <TeamOutlined /> },
        {
          key: '/owner-intelligence/sales-pipeline',
          label: 'Sales pipeline intelligence',
          icon: <LineChartOutlined />,
        },
        { type: 'group', label: 'Operations' },
        { key: '/owner-intelligence/operations', label: 'Operations', icon: <ToolOutlined /> },
        { key: '/owner-intelligence/supply-chain', label: 'Supply Chain', icon: <ShoppingCartOutlined /> },
        { type: 'group', label: 'Intelligence' },
        { key: '/owner-intelligence/risk', label: 'Risk & Opportunities', icon: <WarningOutlined /> },
        { key: '/owner-intelligence/scenarios', label: 'Scenarios', icon: <BranchesOutlined /> },
        { key: '/owner-intelligence/advisor', label: 'AI Advisor', icon: <RobotOutlined /> },
        { key: '/owner-intelligence/ai-governance', label: 'AI Governance', icon: <RobotOutlined /> },
        {
          key: '/owner-intelligence/organizational-knowledge',
          label: 'Organizational Knowledge',
          icon: <FileTextOutlined />,
        },
        { key: '/owner-intelligence/board-packs', label: 'Board & Briefings', icon: <FileTextOutlined /> },
        { type: 'group', label: 'Reports' },
        { key: '/reports', label: 'All reports', icon: <BarChartOutlined /> },
      ],
    },

    edu: {
      label: 'Education',
      items: [
        { type: 'group', label: 'Overview' },
        { key: '/education', label: 'Operations hub', icon: <AppstoreOutlined /> },
        { type: 'group', label: 'Student Administration' },
        { key: '/education/students', label: 'Students', icon: <TeamOutlined /> },
        { key: '/education/admissions', label: 'Admissions', icon: <UserAddOutlined /> },
        { key: '/education/admissions/enquiries', label: 'Enquiries', icon: <SolutionOutlined /> },
        { key: '/education/enrollments', label: 'Enrollment', icon: <FileTextOutlined /> },
        {
          key: '/education/enrollments?view=allocation',
          label: 'Sections & transfers',
          icon: <SwapOutlined />,
        },
        {
          key: '/education/progression',
          label: 'Progression',
          icon: <PartitionOutlined />,
        },
        {
          key: '/education/transfer-certificates',
          label: 'Transfer certificates',
          icon: <FileProtectOutlined />,
        },
        { type: 'group', label: 'Academic Administration' },
        { key: '/education/academic', label: 'Sessions', icon: <CalendarOutlined /> },
        {
          key: '/education/academic?tab=grades',
          label: 'Grades & sections',
          icon: <BankOutlined />,
        },
        {
          key: '/education/academic?tab=subjects',
          label: 'Subjects & offerings',
          icon: <FileTextOutlined />,
        },
        {
          key: '/education/academic?tab=teachers',
          label: 'Teacher assignments',
          icon: <SolutionOutlined />,
        },
        { key: '/education/timetable', label: 'Timetable', icon: <CalendarOutlined /> },
        {
          key: '/education/timetable/smart',
          label: 'Smart Timetable (AI)',
          icon: <RobotOutlined />,
        },
        { key: '/education/substitutions', label: 'Substitutions', icon: <SwapOutlined /> },
        { key: '/education/attendance', label: 'Attendance', icon: <CheckCircleOutlined /> },
        { key: '/education/attendance/calendar', label: 'School calendar', icon: <CalendarOutlined /> },
        { key: '/education/attendance/leave', label: 'Student leave', icon: <ClockCircleOutlined /> },
        {
          key: '/education/attendance/corrections',
          label: 'Corrections',
          icon: <FormOutlined />,
        },
        { type: 'group', label: 'Configuration' },
        { key: '/education/fees', label: 'Fees', icon: <FileTextOutlined /> },
        { key: '/education/transport', label: 'Transport', icon: <CarOutlined /> },
        { key: '/education/library', label: 'Library', icon: <ReadOutlined /> },
        { key: '/education/assessments', label: 'Assessments', icon: <FormOutlined /> },
        { key: '/education/questions', label: 'Question bank', icon: <ReadOutlined /> },
        { key: '/education/activities', label: 'Activities', icon: <TrophyOutlined /> },
        { key: '/education/settings', label: 'Settings', icon: <SettingOutlined /> },
        { key: '/education/policy', label: 'CBSE policy', icon: <SafetyCertificateOutlined /> },
      ],
    },

    platform: {
      label: 'Platform',
      items: [
        { type: 'group', label: 'Security' },
        { key: '/platform/security/users', label: 'Users & Roles', icon: <TeamOutlined /> },
        { key: '/platform/sales-teams', label: 'Sales Teams', icon: <TeamOutlined /> },
        ...(user?.is_super_admin
          ? [
              { type: 'group' as const, label: 'AI' },
              { key: '/platform/ai-agent/config', label: 'AI assistant config', icon: <SettingOutlined /> },
              { key: '/platform/ai-agent/audit', label: 'AI agent audit log', icon: <AuditOutlined /> },
              { key: '/platform/recognition/lab', label: 'Recognition Lab', icon: <CameraOutlined /> },
              { key: '/platform/recognition/analytics', label: 'Recognition analytics', icon: <BarChartOutlined /> },
            ]
          : []),
        { type: 'group', label: 'Workflow' },
        { key: '/platform/workflows/approvals', label: 'Approval Workflows', icon: <BranchesOutlined /> },
        { key: '/platform/workflows/rejection-reasons', label: 'Rejection reasons', icon: <CloseCircleOutlined /> },
        { key: '/platform/rejection-register', label: 'Rejection register', icon: <FileSearchOutlined /> },
        { type: 'group', label: 'System Setup' },
        { key: '/platform/companies', label: 'Companies', icon: <BankOutlined /> },
        { key: '/platform/setup/company', label: 'Company Settings', icon: <BankOutlined /> },
        {
          key: '/platform/setup/configuration-studio',
          label: 'Enterprise Configuration Studio',
          icon: <AppstoreOutlined />,
        },
        { key: '/platform/setup/system', label: 'System Configuration', icon: <SettingOutlined /> },
        { key: '/platform/setup/api-keys', label: 'API Keys', icon: <KeyOutlined /> },
        { key: '/platform/setup/status', label: 'System Status', icon: <HeartOutlined /> },
        { key: '/platform/setup/sequences', label: 'Document Sequences', icon: <NumberOutlined /> },
        { key: '/platform/setup/responsibility-rules', label: 'Responsibility Rules', icon: <FlagOutlined /> },
        { key: '/platform/setup/email-templates', label: 'Email Templates', icon: <MailOutlined /> },
        { key: '/platform/setup/task-sla', label: 'Task SLA rules', icon: <ClockCircleOutlined /> },
        { key: '/platform/documents', label: 'Document library', icon: <FileTextOutlined /> },
        { type: 'group', label: 'Foundation Data' },
        { key: '/platform/foundation/customers', label: 'Customers', icon: <TeamOutlined /> },
        { key: '/platform/foundation/suppliers', label: 'Suppliers', icon: <ShopOutlined /> },
        { key: '/platform/foundation/supplier-categories', label: 'Supplier categories', icon: <TagsOutlined /> },
        { key: '/platform/foundation/items', label: 'Items', icon: <AppstoreOutlined /> },
        { key: '/platform/foundation/warehouses', label: 'Warehouses', icon: <HomeOutlined /> },
        { key: '/platform/foundation/currencies', label: 'Currencies', icon: <DollarOutlined /> },
        { key: '/platform/foundation/revenue-service-types', label: 'Revenue service types', icon: <GoldOutlined /> },
        { type: 'group', label: 'Reports' },
        { key: '/reports', label: 'All reports', icon: <BarChartOutlined /> },
        { key: '/reports/audit', label: 'Report audit', icon: <FileTextOutlined /> },
      ],
    },
  };

  // Get current contextual menu items
  const currentMenuItems = useMemo(() => {
    const menuConfig = contextualMenus[activeModule];
    if (!menuConfig) return [];
    
    const items: any[] = [
      { key: '/', label: 'Dashboard', icon: <DashboardOutlined /> },
      { type: 'divider' },
    ];
    
    // Add favorites section if there are favorites
    const allAvailableItems = Object.values(contextualMenus).flatMap(m => m.items || []);
    const accessibleFavs = favorites.filter(
      (favPath) =>
        canAccessPath(favPath) &&
        allAvailableItems.some((item: any) => item?.key === favPath)
    );
    if (accessibleFavs.length > 0) {
      items.push({ type: 'group', label: 'Favorites' });
      accessibleFavs.forEach(favPath => {
        const favItem = allAvailableItems.find((item: any) => item.key === favPath);
        if (favItem) {
          items.push({
            ...favItem,
            icon: <StarFilled style={{ color: '#faad14' }} />,
          });
        }
      });
      items.push({ type: 'divider' });
    }

    const moduleItems = filterMenuItemsByRouteAccess(
      (menuConfig.items || []) as NonNullable<MenuProps['items']>,
      canAccessPath
    );
    items.push(...(moduleItems || []));

    return translateMenuItems(items as MenuProps['items'], t) || [];
  }, [activeModule, favorites, user?.permissions, user?.is_super_admin, installedPackKeys, t, i18n.language]);

  const breadcrumbBarItems = useMemo(() => {
    const root = {
      title: (
        <Space>
          <DashboardOutlined />
          <span>{t('home', { ns: 'nav', defaultValue: 'Home' })}</span>
        </Space>
      ),
      onClick: () => navigate('/'),
    };

    if (pageBreadcrumbSegments && pageBreadcrumbSegments.length > 0) {
      return [
        root,
        ...pageBreadcrumbSegments.map((seg, index) => {
          const isLast = index === pageBreadcrumbSegments!.length - 1;
          return {
            title: isLast ? <strong>{seg.title}</strong> : seg.title,
            onClick:
              seg.path && !isLast
                ? () => navigate(seg.path!)
                : isLast && seg.path
                  ? () => navigate(seg.path!)
                  : undefined,
          };
        }),
      ];
    }

    const paths = location.pathname.split('/').filter(Boolean);
    const items: Parameters<typeof Breadcrumb>[0]['items'] = [root];
    if (paths.length === 0) return items;

    const uuidSegmentRe =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    let currentPath = '';
    paths.forEach((path, index) => {
      currentPath += `/${path}`;
      const isLast = index === paths.length - 1;
      const decoded = decodeURIComponent(path);
      const isUuid = uuidSegmentRe.test(decoded);
      const title =
        isUuid && isLast
          ? t('details', { ns: 'common', defaultValue: 'Details' })
          : decoded.charAt(0).toUpperCase() + decoded.slice(1).replace(/-/g, ' ');
      items.push({
        title: isLast ? <strong>{title}</strong> : title,
        onClick: !isLast ? () => navigate(currentPath) : undefined,
      });
    });
    return items;
  }, [pageBreadcrumbSegments, location.pathname, navigate, t, i18n.language]);

  // Close mobile menu when route changes
  useEffect(() => {
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Auto-collapse on mobile
  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile]);

  const handleMenuClick = ({ key }: { key: string }) => {
    console.log('[Navigation Debug] Menu clicked:', key);
    console.log('[Navigation Debug] Current location:', location.pathname);
    console.log('[Navigation Debug] Active module:', activeModule);
    navigate(key);
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const sidebarContent = (
    <>
      {/* Mobile Close Button (only show on mobile) */}
      {isMobile && (
        <div 
          style={{ 
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            borderBottom: '1px solid #f0f0f0',
            padding: '0 16px',
          }}
        >
          <CloseOutlined
            role="button"
            tabIndex={0}
            aria-label="Close menu"
            onClick={(e) => {
              e.stopPropagation();
              setMobileMenuOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                setMobileMenuOpen(false);
              }
            }}
            style={{ fontSize: '18px', cursor: 'pointer', padding: '10px' }}
          />
        </div>
      )}

      {/* Menu in scrollable area so sidebar scrolls independently */}
      <div style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
      }}>
        <Menu
          mode="inline"
          selectedKeys={[
            (() => {
              const params = new URLSearchParams(location.search);
              if (
                location.pathname === '/epc/commercial/tenders' &&
                params.get('intent') === 'estimate'
              ) {
                return '/epc/commercial/tenders?intent=estimate';
              }
              if (location.pathname === '/epc/commercial/tenders' && params.get('kind') === 'RFQ') {
                return '/epc/commercial/tenders?kind=RFQ';
              }
              if (location.pathname === '/epc/commercial/tenders' && params.get('kind') === 'RFT') {
                return '/epc/commercial/tenders?kind=RFT';
              }
              if (
                location.pathname === '/epc/commercial/prequalifications' &&
                params.get('kind') === 'EOI'
              ) {
                return '/epc/commercial/prequalifications?kind=EOI';
              }
              if (location.pathname === '/mfg/boms' || location.pathname.startsWith('/mfg/boms/')) {
                return '/mfg/boms';
              }
              if (location.pathname === '/epc/execution') {
                const tab = params.get('tab');
                return tab ? `/epc/execution?tab=${tab}` : '/epc/execution';
              }
              if (location.pathname === '/education/enrollments') {
                if (params.get('view') === 'allocation') {
                  return '/education/enrollments?view=allocation';
                }
                return '/education/enrollments';
              }
              if (location.pathname === '/education/academic') {
                const tab = params.get('tab');
                if (tab === 'grades' || tab === 'subjects' || tab === 'teachers') {
                  return `/education/academic?tab=${tab}`;
                }
                return '/education/academic';
              }
              return location.pathname;
            })(),
          ]}
          items={currentMenuItems as MenuProps['items']}
          onClick={handleMenuClick}
          style={{ borderRight: 0 }}
        />
      </div>
      <div className="no-print" style={{ borderTop: '1px solid #f0f0f0', padding: collapsed && !isMobile ? '8px 0' : '8px 16px', flexShrink: 0 }}>
        <Button
          type="text"
          block
          icon={<QuestionCircleOutlined />}
          title="Help Center — how-to guides"
          onClick={() => {
            navigate('/help');
            if (isMobile) setMobileMenuOpen(false);
          }}
          style={{ textAlign: collapsed && !isMobile ? 'center' : 'left' }}
        >
          {collapsed && !isMobile ? null : 'Help Center'}
        </Button>
      </div>
    </>
  );

  const contentMargin = isMobile ? 12 : isTablet ? 16 : 24;
  const innerPadding = isMobile ? 12 : isTablet ? 16 : 20;
  const breadcrumbMb = isMobile ? 12 : isTablet ? 14 : 16;

  return (
    <Layout className="dashboard-shell" style={{ minHeight: '100vh' }}>
      {/* Command Palette */}
      <CommandPalette open={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />

      {/* Desktop Sidebar */}
      {!isMobile && (
        <div className="dashboard-sider-host">
          <Sider
            theme="light"
            width={240}
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            style={{
              position: 'fixed',
              left: 0,
              top: 50,
              bottom: 0,
              boxShadow: '2px 0 8px rgba(0,0,0,0.05)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
              {sidebarContent}
            </div>
          </Sider>
        </div>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          title={null}
          placement="left"
          closable={false}
          onClose={() => setMobileMenuOpen(false)}
          open={mobileMenuOpen}
          styles={{ body: { padding: 0 } }}
          width={280}
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* Main Layout */}
      <Layout style={{ 
        marginLeft: isMobile ? 0 : (collapsed ? 80 : 240),
        marginTop: 50,
        transition: 'all 0.2s',
      }}>
        {/* Top Navigation Bar */}
        <div className="no-print" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100 }}>
          <TopNavigationBar isMobile={isMobile} isTablet={isTablet} />
        </div>

        {/* Header */}
        <Header
          style={{
            background: '#fff',
            padding: isMobile ? '0 12px' : isTablet ? '0 18px' : '0 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            position: 'sticky',
            top: 50,
            zIndex: 10,
            height: isMobile ? '56px' : '64px',
          }}
        >
          {/* Left side — MODERN UPGRADE: min 44px touch targets + a11y labels */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
            {isMobile ? (
              <span
                role="button"
                tabIndex={0}
                className="dashboard-header-icon-btn"
                aria-label="Open navigation menu"
                onClick={() => setMobileMenuOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setMobileMenuOpen(true);
                  }
                }}
              >
                <MenuUnfoldOutlined style={{ fontSize: '18px', cursor: 'pointer' }} />
              </span>
            ) : (
              <span
                role="button"
                tabIndex={0}
                className="dashboard-header-icon-btn"
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                onClick={() => setCollapsed(!collapsed)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setCollapsed(!collapsed);
                  }
                }}
              >
                {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
                  style: { fontSize: '18px', cursor: 'pointer' },
                })}
              </span>
            )}
            <span
              role="button"
              tabIndex={0}
              className="dashboard-header-icon-btn"
              aria-label="Open command palette"
              onClick={() => setCommandPaletteOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setCommandPaletteOpen(true);
                }
              }}
            >
              <SearchOutlined style={{ fontSize: '18px', cursor: 'pointer', color: '#145a54' }} />
            </span>
            <IntelligenceToolbarButton />
            <CompanySwitcher />
          </div>
        </Header>

        {/* Content */}
        <Content
          className="dashboard-main-content"
          style={{
            margin: contentMargin,
            background: EnterpriseTokens.workspace.surfaceMuted,
            minHeight: 'calc(100dvh - 50px)',
          }}
        >
          {/* Context trail */}
          <div
            className="no-print layout-breadcrumb-bar"
            style={{
              marginBottom: breadcrumbMb,
              padding: isMobile ? '10px 14px' : isTablet ? '11px 16px' : '12px 18px',
              background: '#fff',
              borderRadius: 8,
              border: EnterpriseTokens.workspace.borderSubtle,
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            }}
          >
            <Breadcrumb 
              items={breadcrumbBarItems}
              style={{
                fontSize: isMobile ? '13px' : '14px',
              }}
              separator={<RightOutlined style={{ fontSize: '12px', color: '#8c8c8c' }} />}
            />
          </div>

          {/* Page Content */}
          <div
            className="layout-page-inner"
            style={{
              background: '#fff',
              padding: innerPadding,
              borderRadius: 8,
              border: '1px solid #e8e8e8',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              minHeight: isMobile
                ? 'min(320px, calc(100dvh - 200px))'
                : isTablet
                  ? 'min(400px, calc(100dvh - 230px))'
                  : 'min(480px, calc(100dvh - 250px))',
            }}
          >
            <KeepAliveOutlet />
          </div>
        </Content>

        {/* Footer */}
        <div
          className="no-print"
          style={{
            textAlign: 'center',
            padding: isMobile ? '12px' : '16px',
            background: '#fafafa',
            borderTop: '1px solid #f0f0f0',
          }}
        >
          <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>
            {isMobile ? brand.name : `${brand.name} — Enterprise Operating System`}
          </Text>
        </div>
      </Layout>

      {/* Keyboard Shortcuts Help */}
      <KeyboardShortcutsHelp />
      <PushNotificationListener />
      <AgentDrawer />
      <IntelligenceSidebar />
    </Layout>
  );
};

export const DashboardLayout: React.FC = () => (
  <PageBreadcrumbProvider>
    <AiAgentProvider>
      <DashboardLayoutInner />
    </AiAgentProvider>
  </PageBreadcrumbProvider>
);
